import { Box, styled } from '@ltvco/refresh-lib/theme';

import { DEEPSEARCH_MOBILE_MAX_WIDTH } from '../../../constants';

const mobileQuery = `@media (max-width: ${DEEPSEARCH_MOBILE_MAX_WIDTH}px)`;

export const HeaderGroup = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  marginBottom: 16,
});

export const HeaderRow = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 8,
});

export const HeaderAddressBlock = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  flex: 1,
});

export const HeaderTitle = styled('span')(({ theme }) => ({
  fontSize: 24,
  fontWeight: 700,
  lineHeight: 1.25,
  color: theme.palette.text.primary,
}));

export const HeaderTitleDesktop = styled(HeaderTitle)({
  [mobileQuery]: {
    display: 'none',
  },
});

export const HeaderAddressMobile = styled(Box)({
  display: 'none',
  minWidth: 0,
  flex: 1,
  [mobileQuery]: {
    display: 'block',
  },
});

export const HeaderLocationLine = styled('span')(({ theme }) => ({
  display: 'none',
  fontSize: 16,
  fontWeight: 700,
  lineHeight: 1.35,
  color: theme.palette.text.secondary,
  [mobileQuery]: {
    display: 'block',
  },
}));

export const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginBottom: 16,
  color: theme.palette.rats.main,
}));

export const SectionTitle = styled('span')({
  fontSize: 16,
  fontWeight: 700,
});

export const DetailGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '164px 1fr',
  rowGap: 8,
});

export const DetailLabel = styled('span')(({ theme }) => ({
  fontSize: 16,
  fontWeight: 700,
  color: theme.palette.rats.main,
}));

export const DetailValue = styled('span')(({ theme }) => ({
  fontSize: 16,
  fontWeight: 400,
  color: theme.palette.text.primary,
}));

export const BoldDetailValue = styled('span')(({ theme }) => ({
  fontSize: 16,
  fontWeight: 700,
  color: theme.palette.text.primary,
}));

export const TaxValueRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
});

export const TaxYearSpan = styled('span')(({ theme }) => ({
  fontSize: 16,
  fontWeight: 400,
  color: theme.palette.text.secondary,
}));

export const OwnerRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
});

export const OwnerInfo = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
});

export const OwnerName = styled('span')(({ theme }) => ({
  fontSize: 16,
  fontWeight: 700,
  color: theme.palette.text.primary,
}));

export const OwnerChip = styled('span')({
  fontSize: 14,
  lineHeight: '18px',
  backgroundColor: 'rgba(0, 0, 0, 0.08)',
  borderRadius: 100,
  padding: '3px 10px',
  whiteSpace: 'nowrap',
});

export const LastSoldText = styled('p')(({ theme }) => ({
  fontSize: 16,
  color: theme.palette.text.secondary,
  margin: '12px 0 0',
}));

export const LastSoldValue = styled('span')(({ theme }) => ({
  fontWeight: 700,
  color: theme.palette.text.primary,
}));

export const RecordItemHead = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  gap: 10,
  alignItems: 'baseline',
});

export const RecordItemTitle = styled('span')({
  fontSize: 14,
  fontWeight: 700,
});

export const RecordItemDate = styled('span')(({ theme }) => ({
  fontSize: 12.5,
  color: theme.palette.text.secondary,
  whiteSpace: 'nowrap',
}));

export const RecordItemBody = styled('p')(({ theme }) => ({
  margin: '8px 0 0',
  fontSize: 13,
  lineHeight: 1.45,
  color: theme.palette.text.secondary,
}));

export const RecordItemMeta = styled(Box)(({ theme }) => ({
  marginTop: 8,
  fontSize: 12,
  color: theme.palette.text.disabled,
}));

export const KvRow = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  gap: 14,
  alignItems: 'baseline',
  fontSize: 14.5,
  lineHeight: 1.4,
});

export const KvValue = styled('span')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  gap: 4,
  textAlign: 'right',
  fontWeight: 700,
  color: theme.palette.text.primary,
}));

export const KvSub = styled('span')(({ theme }) => ({
  display: 'block',
  fontWeight: 400,
  fontSize: 11.5,
  lineHeight: 1.35,
  color: theme.palette.text.disabled,
}));

export const SubHead = styled('div')(({ theme }) => ({
  fontSize: 13,
  fontWeight: 700,
  color: theme.palette.text.primary,
  margin: '0 0 6px',
}));

export const TaxTableWrapper = styled(Box)(({ theme }) => ({
  marginTop: 14,
  width: '100%',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 8,
  overflowX: 'auto',
  WebkitOverflowScrolling: 'touch',
}));

export const TaxTableGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns:
    '52px minmax(96px, 1.1fr) minmax(72px, 0.9fr) minmax(96px, 1fr) minmax(104px, 1.1fr)',
  minWidth: 560,
  width: '100%',
});

export const TaxTableHeadCell = styled('div', {
  shouldForwardProp: (prop) => prop !== 'separator' && prop !== 'align',
})<{ separator?: boolean; align?: 'left' | 'right' }>(
  ({ theme, separator, align = 'left' }) => ({
    fontSize: 12.5,
    fontWeight: 700,
    lineHeight: 1.3,
    color: theme.palette.text.primary,
    padding: '10px 8px',
    whiteSpace: 'nowrap',
    textAlign: align,
    ...(separator && {
      borderLeft: `1px solid ${theme.palette.divider}`,
    }),
    [mobileQuery]: {
      whiteSpace: 'normal',
      minWidth: 72,
    },
  })
);

export const TaxTableCell = styled('div', {
  shouldForwardProp: (prop) =>
    prop !== 'striped' &&
    prop !== 'separator' &&
    prop !== 'muted' &&
    prop !== 'align',
})<{
  striped?: boolean;
  separator?: boolean;
  muted?: boolean;
  align?: 'left' | 'right';
}>(({ theme, striped, separator, muted, align = 'left' }) => ({
  fontSize: 13,
  color: muted ? theme.palette.text.disabled : theme.palette.text.primary,
  padding: '10px 8px',
  whiteSpace: 'nowrap',
  textAlign: align,
  backgroundColor: striped ? theme.palette.background.default : 'transparent',
  ...(separator && {
    borderLeft: `1px solid ${theme.palette.divider}`,
  }),
}));

export const ResidentRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  minHeight: 44,
  padding: '4px 0',
});

export const NextStepsTitle = styled('div')(({ theme }) => ({
  fontSize: 16,
  fontWeight: 700,
  color: theme.palette.text.primary,
  marginBottom: 4,
}));

export const NextStepButton = styled('button')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 9,
  width: '100%',
  minHeight: 44,
  textAlign: 'left',
  background: 'none',
  border: 'none',
  padding: '8px 0',
  margin: 0,
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: 16,
  fontWeight: 600,
  color: theme.palette.text.primary,
  lineHeight: 1.4,
  '&:hover': { color: theme.palette.primary.main },
}));

export const ShowMoreButton = styled('button')(({ theme }) => ({
  marginTop: 8,
  background: 'none',
  border: 'none',
  padding: '4px 0',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: 14,
  fontWeight: 700,
  color: theme.palette.primary.main,
}));
